/// <reference types="../../@types/post-robot" />
import postRobot, { ResponseMessageEvent } from 'post-robot';
import { methods } from './methods/Index';
import { logger, LogEvents } from '../helpers/Logger';
import { Timeouts } from '../helpers/constants/Timeouts';
import { history } from '../helpers/History';
import { StoreModel } from '../models/Index';
import { RoutePaths } from '../helpers/constants/RoutePaths';
import { EventEmitter } from './EventEmitter';
import { CashAcceptorStatusCode, BusMessage } from '@b2bsoft/kiosk-shell-sdk';
import { KioskApplication } from 'models/common/KioskApplication';
import { ApplicationCloseReason, Sdk, SdkEvents } from './Sdk';

export * from './Sdk';

export function registerSDK(store: StoreModel): Sdk {
    const { configuration } = store
    const options = {
        domain: configuration.app.allowedDomainsForSdk,
    };

    Object.keys(methods)
        .forEach(method => {
            postRobot.on(method, options, (...args) => methods[method](store, ...args));
        });
    return createSDK(store);
}

export function createSDK(store: StoreModel): Sdk {
    const eventEmitter = new EventEmitter();

    return {
        currentAppFrame: null,
        application: null,
        events: eventEmitter,

        /**
         * This method should be used for opening sub applications, since we have handshake process
         * Handshake process is required to check if application is opened correctly, in case if not
         * it will throw error, and we will know that application is not available right now.
         * We really don't want to have stucked KIOSK and have "white screens"
         * @param {*} target HTMLIFrameElement which represents opened application frame
         * @returns {Promise<any>}
         */
        openApp(application: KioskApplication, target: any): Promise<any> {
            store.loader.show();

            let promise = Promise.resolve({});

            if (application.enableHandshake) {
                promise = postRobot
                    .send(target, 'handshake', {}, { timeout: Timeouts.SdkOpenApp })
                    .catch(error => {
                        console.error(`Application ${application.applicationName} was not able to open. Please try again...`, error)

                        history.push({
                            pathname: store.common.hasMultiplyApplications
                                ? RoutePaths.ApplicationSelect
                                : RoutePaths.IdleScreen
                        });

                        store.snackbar.showMessage("error", `Application ${application.applicationName} was not able to open. Please try again...`);
                        store.loader.hide()
                        
                        // Wait until redirect will happen
                        return new Promise((resolve, reject) => { });
                    }) as Promise<ResponseMessageEvent>;
            }

            return promise
                .then((event: any) => {
                    this.currentAppFrame = target;
                    this.application = application;
                    this.events.emit(SdkEvents.ApplicationOpened, event.data);

                    return event.data;
                })
                .finally(() => {
                    store.loader.hide()
                })
        },

        /**
         * This method should be used for graceful closing sub applications.
         * This is required for Sub Applications, since they can handle some important staff before closing
         * @returns {Promise<any>}
         */
        closeApp(reason: ApplicationCloseReason): Promise<any> {
            if (!this.currentAppFrame
                || !this.application) {
                return Promise.resolve();
            }

            logger.logInformation(`Closing application ${this.application.applicationName||''} by reason ${reason}`, LogEvents.ClosingApp, { reason })

            let promise = Promise.resolve({} as any);

            if (this.application.enableGracefulClose) {
                promise = postRobot
                    .send(this.currentAppFrame!, 'closeRequest', { reason: reason }, { timeout: Timeouts.SdkCloseApp });
            }

            return promise
                .then(event => {
                    this.currentAppFrame = null;
                    this.application = null;
                    this.events.emit(SdkEvents.ApplicationClosed, event.data);
                })
                .catch(error => {
                    console.error("Failed to close app", error);

                    throw error;
                });
        },

        cashAcceptorStatusChanged(status: CashAcceptorStatusCode) {
            if (!this.currentAppFrame) {
                return Promise.resolve();
            }

            return postRobot
                .send(this.currentAppFrame!, 'cashAcceptorStatusChanged', status);
        },

        transactionChanged(data: BusMessage<any>) {
            if (!this.currentAppFrame) {
                return Promise.resolve();
            }

            return postRobot
                .send(this.currentAppFrame!, 'transactionChanged', data);
        },

        userActivity() {
            if (!this.currentAppFrame) {
                return Promise.resolve();
            }

            return postRobot
                .send(this.currentAppFrame!, 'userActivity');
        }
    }
}
